import { glMatrix, vec2, vec4 } from 'gl-matrix';

import { type ReadonlyCubicBezier } from './types';

type AddPointsFunction<T> = (
  points: T[],
  p0X: number,
  p0Y: number,
  p1X: number,
  p1Y: number,
  p2X: number,
  p2Y: number,
  p3X: number,
  p3Y: number,
  tStart: number,
  tEnd: number,
) => void;

/**
 * Generates a set of points which represent a cubic Bezier curve with sufficient enough
 * detail to be used to render them (imagine drawing small lines between the points, for
 * example).
 *
 * @description Uses recursive splitting to split a Bezier curve into smaller curves
 * until the points generated are 'flat enough' between each other to form line segments
 * which can be sufficiently used for rendering.
 * @description Flatness is determined by the perpendicular distance between the two
 * control points and the line formed by p0 -> p3 on the bezier. See:
 * {@link isFlatEnough} for a complete description.
 *
 * @param points Array to store the points in.
 * @param bezier Cubic Bezier curve to convert to points.
 * @param tolerance % of 1 radian we accept as tolerance.
 * @param maxDepth Maximum recursion depth to prevent infinite recursion.
 */
export function bezierToPoints(
  points: vec2[],
  bezier: ReadonlyCubicBezier,
  tolerance = 0.1,
  maxDepth = 10,
): vec2[] {
  const p0 = bezier.start;
  const p1 = bezier.control1;
  const p2 = bezier.control2;
  const p3 = bezier.end;

  return bezierToPointsFlat(
    points,
    addPointsToCurve,
    p0[0],
    p0[1],
    p1[0],
    p1[1],
    p2[0],
    p2[1],
    p3[0],
    p3[1],
    tolerance,
    0,
    1,
    maxDepth,
  );
}

/**
 * Generates a set of points which represent a cubic Bezier curve with sufficient enough
 * detail to be used to render them (imagine drawing small lines between the points, for
 * example). Includes normals for each point.
 *
 * @description Uses recursive splitting to split a Bezier curve into smaller curves
 * until the points generated are 'flat enough' between each other to form line segments
 * which can be sufficiently used for rendering.
 * @description Flatness is determined by the perpendicular distance between the two
 * control points and the line formed by p0 -> p3 on the bezier. See:
 * {@link isFlatEnough} for a complete description.
 *
 * @param points Array to store the points in and normals.
 * @param bezier Cubic Bezier curve to convert to points and normals.
 * @param tolerance % of 1 radian we accept as tolerance.
 * @param maxDepth Maximum recursion depth to prevent infinite recursion.
 */
export function bezierToPointsWithNormals(
  points: vec4[],
  bezier: ReadonlyCubicBezier,
  tolerance = 0.1,
  maxDepth = 10,
): vec4[] {
  const p0 = bezier.start;
  const p1 = bezier.control1;
  const p2 = bezier.control2;
  const p3 = bezier.end;

  return bezierToPointsFlat(
    points,
    addPointsToCurveWithNormal,
    p0[0],
    p0[1],
    p1[0],
    p1[1],
    p2[0],
    p2[1],
    p3[0],
    p3[1],
    tolerance,
    0,
    1,
    maxDepth,
  );
}

/**
 * Generates a set of points which represent a cubic Bezier curve with sufficient enough
 * detail to be used to render them (imagine drawing small lines between the points, for
 * example). The points are represented only by their t-parameters, rather than a new vec2/vec4.
 *
 * @description Uses recursive splitting to split a Bezier curve into smaller curves
 * until the points generated are 'flat enough' between each other to form line segments
 * which can be sufficiently used for rendering.
 * @description Flatness is determined by the perpendicular distance between the two
 * control points and the line formed by p0 -> p3 on the bezier. See:
 * {@link isFlatEnough} for a complete description.
 *
 * @param points Array to store the t-parameters of each point in.
 * @param bezier Cubic Bezier curve to convert to points.
 * @param tolerance % of 1 radian we accept as tolerance.
 * @param maxDepth Maximum recursion depth to prevent infinite recursion.
 */
export function bezierToPointsByTParameter(
  points: number[],
  bezier: ReadonlyCubicBezier,
  tolerance = 0.1,
  maxDepth = 10,
): number[] {
  const p0 = bezier.start;
  const p1 = bezier.control1;
  const p2 = bezier.control2;
  const p3 = bezier.end;

  return bezierToPointsFlat(
    points,
    addPointsToCurveByTParameter,
    p0[0],
    p0[1],
    p1[0],
    p1[1],
    p2[0],
    p2[1],
    p3[0],
    p3[1],
    tolerance,
    0,
    1,
    maxDepth,
  );
}

/**
 * Internal function to recursively compute points for a cubic Bezier curve. Uses flat
 * number arguments for control points so that recursive calls only allocate data on the
 * stack, which is much faster than allocating objects on the heap.
 *
 * @param points Array to store the points in.
 * @param addToCurveFn Function to add points to the curve.
 */
function bezierToPointsFlat<T>(
  points: T[],
  addToCurveFn: AddPointsFunction<T>,
  p0X: number,
  p0Y: number,
  p1X: number,
  p1Y: number,
  p2X: number,
  p2Y: number,
  p3X: number,
  p3Y: number,
  tolerance: number,
  tStart: number,
  tEnd: number,
  maxDepth: number,
) {
  // If we've reached max depth or the curve is flat enough, add the points
  if (maxDepth <= 0 || isFlatEnough(p0X, p0Y, p1X, p1Y, p2X, p2Y, p3X, p3Y, tolerance)) {
    addToCurveFn(points, p0X, p0Y, p1X, p1Y, p2X, p2Y, p3X, p3Y, tStart, tEnd);
    return points;
  }

  // between control points
  const q0X = (p0X + p1X) / 2;
  const q0Y = (p0Y + p1Y) / 2;
  const q1X = (p1X + p2X) / 2;
  const q1Y = (p1Y + p2Y) / 2;
  const q2X = (p2X + p3X) / 2;
  const q2Y = (p2Y + p3Y) / 2;

  // between intermediate points
  const r0X = (q0X + q1X) / 2;
  const r0Y = (q0Y + q1Y) / 2;
  const r1X = (q1X + q2X) / 2;
  const r1Y = (q1Y + q2Y) / 2;

  // point closest to curve
  const sX = (r0X + r1X) / 2;
  const sY = (r0Y + r1Y) / 2;

  bezierToPointsFlat(
    points,
    addToCurveFn,
    p0X,
    p0Y,
    q0X,
    q0Y,
    r0X,
    r0Y,
    sX,
    sY,
    tolerance,
    tStart,
    (tStart + tEnd) / 2,
    maxDepth - 1,
  );
  bezierToPointsFlat(
    points,
    addToCurveFn,
    sX,
    sY,
    r1X,
    r1Y,
    q2X,
    q2Y,
    p3X,
    p3Y,
    tolerance,
    (tStart + tEnd) / 2,
    tEnd,
    maxDepth - 1,
  );

  return points;
}

/**
 * Adds points to represent a curve segment, handling the case where the first point
 * might already exist from a previous recursion.
 */
function addPointsToCurve(
  points: vec2[],
  p0X: number,
  p0Y: number,
  _p1X: number,
  _p1Y: number,
  _p2X: number,
  _p2Y: number,
  p3X: number,
  p3Y: number,
) {
  const last = points.length > 1 ? points[points.length - 1] : undefined;
  if (last && glMatrix.equals(last[0], p0X) && glMatrix.equals(last[1], p0Y)) {
    points.push(storePoint(p3X, p3Y, vec2.create()));
  } else {
    points.push(storePoint(p0X, p0Y, vec2.create()), storePoint(p3X, p3Y, vec2.create()));
  }
}

/**
 * Adds points to represent a curve segment, handling the case where the first point
 * might already exist from a previous recursion. Also computes the normal vector at
 * each point.
 */
function addPointsToCurveWithNormal(
  points: vec4[],
  p0X: number,
  p0Y: number,
  p1X: number,
  p1Y: number,
  p2X: number,
  p2Y: number,
  p3X: number,
  p3Y: number,
  tStart: number,
  tEnd: number,
) {
  const last = points.length > 1 ? points[points.length - 1] : undefined;
  if (last && glMatrix.equals(last[0], p0X) && glMatrix.equals(last[1], p0Y)) {
    const point = vec4.create();
    storePoint(p3X, p3Y, point);
    storeBezierNormal(p0X, p0Y, p1X, p1Y, p2X, p2Y, p3X, p3Y, tEnd, point);
    points.push(point);
  } else {
    const firstPoint = vec4.create();
    const lastPoint = vec4.create();

    storePoint(p0X, p0Y, firstPoint);
    storeBezierNormal(p0X, p0Y, p1X, p1Y, p2X, p2Y, p3X, p3Y, tStart, firstPoint);

    storePoint(p3X, p3Y, lastPoint);
    storeBezierNormal(p0X, p0Y, p1X, p1Y, p2X, p2Y, p3X, p3Y, tEnd, lastPoint);

    points.push(firstPoint, lastPoint);
  }
}

/**
 * Adds points to represent a curve segment, handling the case where the first point
 * might already exist from a previous recursion. The points are represented only by
 * their t-parameters, rather than a new vec2/vec4.
 */
function addPointsToCurveByTParameter(
  points: number[],
  _p0X: number,
  _p0Y: number,
  _p1X: number,
  _p1Y: number,
  _p2X: number,
  _p2Y: number,
  _p3X: number,
  _p3Y: number,
  tStart: number,
  tEnd: number,
) {
  const last = points.length > 1 ? points[points.length - 1] : undefined;
  if (last !== undefined && glMatrix.equals(last, tStart)) {
    points.push(tEnd);
  } else {
    points.push(tStart, tEnd);
  }
}

/**
 * Compares the line formed by `start` and `end` with the two control points
 * `controlPoint1` and `controlPoint2` on a cubic Bezier curve. If these are sufficiently
 * close (less than {@link tolerance}), then we can use these points to form a straight
 * line on the curve.
 * @param startX The x-coordinate of the start point.
 * @param startY The y-coordinate of the start point.
 * @param controlPoint1X The x-coordinate of the first control point.
 * @param controlPoint1Y The y-coordinate of the first control point.
 * @param controlPoint2X The x-coordinate of the second control point.
 * @param controlPoint2Y The y-coordinate of the second control point.
 * @param endX The x-coordinate of the end point.
 * @param endY The y-coordinate of the end point.
 * @param tolerance The maximum distance from the line to the control points for them to
 * be considered flat enough.
 * @returns `true` if the curve is flat enough to be represented as a line segment,
 * `false` otherwise.
 */
function isFlatEnough(
  startX: number,
  startY: number,
  controlPoint1X: number,
  controlPoint1Y: number,
  controlPoint2X: number,
  controlPoint2Y: number,
  endX: number,
  endY: number,
  tolerance: number,
) {
  // take the 'farthest' distance - this is the worst case
  const maxDistanceToLine = Math.max(
    pointToLineDistance(startX, startY, endX, endY, controlPoint1X, controlPoint1Y),
    pointToLineDistance(startX, startY, endX, endY, controlPoint2X, controlPoint2Y),
  );

  return maxDistanceToLine <= tolerance;
}

/**
 * Uses the standard perpendicular distance formula
 * {@link https://en.wikipedia.org/wiki/Distance_from_a_point_to_a_line} to compute the
 * shortest distance from a point to a line.
 * @param aX The x-coordinate of point 1 on the line.
 * @param aY The y-coordinate of point 1 on the line.
 * @param bX The x-coordinate of point 2 on the line.
 * @param bY The y-coordinate of point 2 on the line.
 * @param pX The x-coordinate of the point to compute distance for.
 * @param pY The y-coordinate of the point to compute distance for.
 * @returns Perpendicular distance from point to the line formed by a -> b.
 */
function pointToLineDistance(
  aX: number,
  aY: number,
  bX: number,
  bY: number,
  pX: number,
  pY: number,
) {
  const abX = bX - aX;
  const abY = bY - aY;
  const apX = pX - aX;
  const apY = pY - aY;

  const length = Math.hypot(abX, abY);
  // If the length is zero-ish (since its a float), we fail out and just return 0
  if (length < 0.0001) {
    return 0;
  }

  return (
    // numerator
    Math.abs(abX * apY - abY * apX) / // denominator
    length
  );
}

/**
 * Stores a point into the first two positions of a vec4.
 */
function storePoint<T extends vec2 | vec4 = vec2>(vX: number, vY: number, out: T) {
  out[0] = vX;
  out[1] = vY;
  return out;
}

/**
 * Compute & store the normal (first derivative) of a cubic Bezier curve at parameter t
 *
 * @param p0X The x-coordinate of the start point.
 * @param p0Y The y-coordinate of the start point.
 * @param p1X The x-coordinate of the first control point.
 * @param p1Y The y-coordinate of the first control point.
 * @param p2X The x-coordinate of the second control point.
 * @param p2Y The y-coordinate of the second control point.
 * @param p3X The x-coordinate of the end point.
 * @param p3Y The y-coordinate of the end point.
 * @param t The parameter at which to compute the normal.
 * @returns normal vector at point t
 */
function storeBezierNormal(
  p0X: number,
  p0Y: number,
  p1X: number,
  p1Y: number,
  p2X: number,
  p2Y: number,
  p3X: number,
  p3Y: number,
  t: number,
  out = vec4.create(),
) {
  const mt = 1 - t;
  const mt2 = mt * mt;
  const t2 = t * t;

  const dx = 3 * mt2 * (p1X - p0X) + 6 * mt * t * (p2X - p1X) + 3 * t2 * (p3X - p2X);
  const dy = 3 * mt2 * (p1Y - p0Y) + 6 * mt * t * (p2Y - p1Y) + 3 * t2 * (p3Y - p2Y);

  // The normal is the negative reciprocal of the tangent
  const normal = vec2.fromValues(-dy, dx);
  const normalUnit = vec2.normalize(normal, normal);

  out[2] = normalUnit[0];
  out[3] = normalUnit[1];

  return out;
}
